import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { iCollection, patchCollection } from "@/services/collection";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

export default function LoyaltySettings({
  collection,
  onRefresh,
}: {
  collection: iCollection;
  onRefresh(): void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [percentageCode, setPercentageCode] = useState<string>("");
  const [percentageError, setPercentageError] = useState<string>("");
  const [isLoyaltyEnabled, setIsLoyaltyEnabled] = useState<boolean>(false);
  const [isActiveSaveBtn, setIsActiveSaveBtn] = useState<boolean>(false);
  const [confirmCode, setConfirmCode] = useState<boolean>(false);

  const handleExit = () => {
    navigate(`/collection/${collection._id}/taks`);
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validate percentage code
    const percentNum = Number(percentageCode);
    if (isNaN(percentNum) || percentNum < 5 || percentNum > 100) {
      setPercentageError(t("loyalty.validation.percentage_range"));
      return;
    } else {
      setPercentageError("");
    }

    setIsLoading(true);
    try {
      if (isLoyaltyEnabled) {
        await patchCollection({
          loyatyDiscountPercentage: Number(percentageCode),
          isLoyaltyEnabled: isLoyaltyEnabled,
          collectionId: collection._id,
        });
      } else {
        await patchCollection({
          isLoyaltyEnabled: isLoyaltyEnabled,
          collectionId: collection._id,
        });
      }
      onRefresh();

      navigate(`/collection/${collection._id}/taks`);
    } catch (error) {
      console.error("Error saving loyalty data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setIsLoyaltyEnabled(collection.isLoyaltyEnabled === true);
    if (collection.loyatyDiscountPercentage) {
      setPercentageCode(collection.loyatyDiscountPercentage.toString());
    }
  }, [collection]);

  useEffect(() => {
    if (isLoyaltyEnabled) {
      if (confirmCode) {
        setIsActiveSaveBtn(true);
      } else {
        setIsActiveSaveBtn(false);
      }
    } else {
      setConfirmCode(false);
      setIsActiveSaveBtn(true);
    }
  }, [isLoyaltyEnabled, confirmCode]);

  if (isLoading) {
    return (
      <div className="flex flex-col px-6 py-10 md:px-14 md:py-8 min-h-[calc(100dvh-theme(spacing.16)-theme(spacing.16))]">
        <div className="flex flex-col space-y-3">
          <Skeleton className="bg-gray-200 h-[32px] rounded-lg" />
          <Skeleton className="bg-gray-200 h-[32px] rounded-lg" />
          <Skeleton className="bg-gray-200 h-[32px] rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 py-10 md:px-14 md:py-8 min-h-[calc(100dvh-theme(spacing.16)-theme(spacing.16))]">
      {/* Header */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <p className="text-2xl font-medium">
              {t("loyalty.settings_title")}
            </p>
          </div>
          <Button variant="default" onClick={handleExit}>
            {t("loyalty.exit")}
            <X size={16} className="ml-2" />
          </Button>
        </div>

        <Separator className="my-6" />
      </div>
      {/* Content */}
      <div className="flex-1">
        <div>
          <div>
            <p className="text-xl font-medium">
              {t("loyalty.settings_title_sub")}
            </p>
            <p className="text-base text-gray-600 font-medium mb-11">
              {t("loyalty.settings_description")}
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-11">
              <p className="text-base font-medium min-w-[335px]">
                {isLoyaltyEnabled
                  ? t("loyalty.settings_code_active")
                  : t("loyalty.settings_code_noactive")}
              </p>
              <Switch
                checked={isLoyaltyEnabled}
                onCheckedChange={setIsLoyaltyEnabled}
                className="data-[state=checked]:bg-takyon"
              />
            </div>
            <div className={`opacity-${isLoyaltyEnabled ? 100 : 50}`}>
              <form onSubmit={onSubmit} className="space-y-6" id="loyaltyForm">
                {/* Simple percentage input */}
                <div className="mb-4">
                  <div className="flex flex-col mb-1">
                    <p className="text-sm font-medium">
                      {t("loyalty.percentage_code")}
                    </p>
                  </div>
                  <div
                    className="relative flex items-center gap-1"
                    style={{ marginTop: "0px" }}
                  >
                    <Input
                      type="text"
                      inputMode="numeric"
                      disabled={!isLoyaltyEnabled}
                      placeholder={t(
                        "loyalty.how_it_works.percentage_placeholder",
                      )}
                      className="pr-8 pt-2 w-96"
                      value={percentageCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, "");
                        setPercentageCode(value);
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/^\d$/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <span>%</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t("loyalty.percentage_code_sub")}
                  </p>
                  {percentageError && (
                    <p className="text-sm text-red-500 mt-1">
                      {percentageError}
                    </p>
                  )}
                </div>
              </form>
              <div className="flex flex-wrap gap-2 items-center mt-[32px]">
                <Checkbox
                  id="confirmCode"
                  name="confirmCode"
                  disabled={!isLoyaltyEnabled}
                  checked={confirmCode}
                  onCheckedChange={(checked) =>
                    setConfirmCode(checked === true)
                  }
                />
                <p className="text-sm font-medium">
                  {t("loyalty.settings_accept")}
                </p>
              </div>
            </div>
          </div>
          <div className="w-full flex justify-end">
            <Button
              variant={"takyon"}
              type="submit"
              className="mt-4"
              form="loyaltyForm"
              disabled={!isActiveSaveBtn}
            >
              {t("buttons.save_and_continue")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
